import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";

import { COUNTRIES, filterCountries } from "@/lib/countries";

/**
 * Wave 14e/5 (ROADS-339): hand-rolled 200ms debounce. We don't pull in
 * `lodash.debounce` because we don't need it — this is a single component
 * with one debounced value. Keeps the bundle slim.
 */
const COUNTRY_SEARCH_DEBOUNCE_MS = 200;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

/**
 * apps/web :: components/onboarding :: HomeCountryStep
 *
 * Step 1 of the onboarding wizard. Lets the user pick their home country via
 * a type-to-filter list. Stores the ISO 3166-1 alpha-2 code (uppercase) —
 * the backend allow-list is case-sensitive.
 *
 * Filtering is `startsWith` on either the English display name or the code
 * (so `IN` narrows to India / Indonesia, not every country with "in" in
 * the middle of its name). Empty query → full catalog.
 *
 * Accessibility:
 *   - The list is a `role="listbox"` with `role="option"` rows.
 *   - Each row has an `aria-selected` reflecting the current choice.
 *   - Keyboard users can tab into the input, type to filter, then tab into
 *     the list. ArrowUp/Down / Home / End are handled so screen-reader
 *     users can navigate without a mouse.
 */
export interface HomeCountryStepProps {
  value: string | null;
  onNext: (code: string) => void;
  /**
   * Optional back handler. ROADS-508 promoted `native_language` to step 1,
   * so this step (now step 2) gets a Back button to return to it. When
   * omitted, the button is suppressed — preserves the original behaviour
   * for any future caller that mounts this step as the very first one.
   */
  onBack?: () => void;
}

export default function HomeCountryStep({ value, onNext, onBack }: HomeCountryStepProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(value);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, COUNTRY_SEARCH_DEBOUNCE_MS);
  // True while the user has typed but the debounce hasn't yet fired.
  // Drives both the aria-live "Searching…" hint and the polite-region
  // testid below.
  const isDebouncing = query !== debouncedQuery;
  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Filter against the debounced value so a fast 5-keystroke burst only
  // hits filterCountries() once. The keystroke feels instant; the
  // expensive O(n) filter is the only thing we delay.
  const filtered = useMemo(
    () => filterCountries(debouncedQuery, COUNTRIES),
    [debouncedQuery],
  );

  function handleKeyDown(
    ev: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    switch (ev.key) {
      case "ArrowDown": {
        ev.preventDefault();
        const next = filtered[Math.min(filtered.length - 1, index + 1)];
        if (next) optionRefs.current[next.code]?.focus();
        return;
      }
      case "ArrowUp": {
        ev.preventDefault();
        const prev = filtered[Math.max(0, index - 1)];
        if (prev) optionRefs.current[prev.code]?.focus();
        return;
      }
      case "Home": {
        ev.preventDefault();
        const first = filtered[0];
        if (first) optionRefs.current[first.code]?.focus();
        return;
      }
      case "End": {
        ev.preventDefault();
        const last = filtered[filtered.length - 1];
        if (last) optionRefs.current[last.code]?.focus();
        return;
      }
      default:
        return;
    }
  }

  const canProceed = draft !== null && draft.length > 0;

  return (
    <div className="space-y-md">
      <header>
        <h2 className="font-display text-xl font-semibold text-brand-primary">
          {t("onboarding.homeCountry.title")}
        </h2>
        <p className="mt-xs text-sm text-neutral-700">
          {t("onboarding.homeCountry.body")}
        </p>
      </header>

      <label className="flex flex-col gap-xs">
        <span className="text-xs font-medium text-neutral-700">
          {t("onboarding.homeCountry.searchLabel")}
        </span>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="home-country-search"
          placeholder={t("onboarding.homeCountry.searchPlaceholder") as string}
          autoComplete="off"
          className="rounded-md border border-brand-primary/20 bg-white px-sm py-xs text-sm text-neutral-900 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
        />
      </label>

      {/*
        Wave 14e/5: polite live region so screen-reader users hear that
        the catalog is being narrowed. Empty when idle so AT doesn't get
        announced on every render. We render the testid even while empty
        for a stable querySelector target in the test.
      */}
      <span
        role="status"
        aria-live="polite"
        data-testid="home-country-search-status"
        className="sr-only"
      >
        {isDebouncing ? t("onboarding.homeCountry.searching") : ""}
      </span>

      {filtered.length === 0 ? (
        <p
          data-testid="home-country-empty"
          className="rounded-md border border-brand-primary/10 bg-white p-md text-xs text-neutral-600"
        >
          {t("onboarding.homeCountry.emptyHint")}
        </p>
      ) : (
        <div
          role="listbox"
          aria-label={t("onboarding.homeCountry.listLabel")}
          data-testid="home-country-listbox"
          className="max-h-64 overflow-y-auto rounded-md border border-brand-primary/10 bg-white"
        >
          {filtered.map((c, index) => {
            const selected = draft === c.code;
            return (
              <button
                key={c.code}
                ref={(el) => {
                  optionRefs.current[c.code] = el;
                }}
                type="button"
                role="option"
                aria-selected={selected}
                data-testid={`home-country-option-${c.code}`}
                onClick={() => setDraft(c.code)}
                onKeyDown={(ev) => handleKeyDown(ev, index)}
                className={[
                  "flex w-full items-center justify-between gap-sm px-sm py-xs text-left text-sm transition-colors",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-primary",
                  selected
                    ? "bg-brand-primary/10 text-brand-primary"
                    : "text-neutral-800 hover:bg-brand-primary/5",
                ].join(" ")}
              >
                <span>{c.name}</span>
                <span
                  aria-hidden="true"
                  className="inline-flex h-5 min-w-[2rem] items-center justify-center rounded-md bg-brand-primary/10 px-xs text-[10px] font-semibold text-brand-primary"
                >
                  {c.code}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!canProceed && query !== "" && filtered.length > 0 && (
        <p className="text-xs text-neutral-600">
          {t("onboarding.homeCountry.required")}
        </p>
      )}

      <div
        className={`flex items-center gap-sm pt-md ${
          onBack ? "justify-between" : "justify-end"
        }`}
      >
        {onBack && (
          <button
            type="button"
            data-testid="onboarding-back"
            onClick={onBack}
            className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary transition-colors hover:border-brand-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("onboarding.back")}
          </button>
        )}
        <button
          type="button"
          data-testid="onboarding-next"
          disabled={!canProceed}
          onClick={() => {
            if (!canProceed || draft === null) return;
            onNext(draft);
          }}
          className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("onboarding.next")}
        </button>
      </div>
    </div>
  );
}
